import { useCallback, useEffect, useState } from "react";
import { PaymentMethodE } from "../../../../Api/dto/Competition_POST_PUT";
import {
  VStack,
  Text,
  Box,
  FormControl,
  FormLabel,
  Input,
  FormHelperText,
  Textarea,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Center,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { SignupClient } from "../../../../Api/client/CompetitionClient";
import {
  DisciplineSignupDto_POST_PUT,
  InvoiceContactInfo,
  SignupRequest,
} from "../../../../Api/dto/SignupRequest_POST";
import { Role, useSessionState } from "../../../../Session/SessionService";

export interface PaymentMethodSubstepState {
  lastFetchPaymentMethod: PaymentMethodE | null;
  lastFetchDiscountSecret: string | null;
  summary: string;
  cost?: number;
  invoiceContactInfo: InvoiceContactInfo;
  valid: boolean;
}

interface StateAndModifiers {
  state: PaymentMethodSubstepState;
  modifiers: {
    reset: () => void;
    fetchCostCalculation: (
      paymentMethod: PaymentMethodE,
      discountKey: string,
      signupRequests: Array<SignupRequest>,
      competitionTag: string
    ) => void;
    setInvoiceContactInfo: (
      contactInfo: InvoiceContactInfo,
      valid: boolean
    ) => void;
    overrideCost: (newCost: number | undefined) => void;
  };
}

const INITIAL_STATE: PaymentMethodSubstepState = {
  lastFetchDiscountSecret: null,
  lastFetchPaymentMethod: null,
  cost: 0,
  summary: "",
  invoiceContactInfo: {},
  valid: false,
};

export function usePaymentDetailsState(paymentMethod: PaymentMethodE | null) {
  const [state, setState] = useState<PaymentMethodSubstepState>({
    ...INITIAL_STATE,
    valid: validateContactInfo({}, paymentMethod).validForPaymentMethod,
  });

  useEffect(() => {
    setState((old) => ({
      ...old,
      valid: validateContactInfo(old.invoiceContactInfo, paymentMethod)
        .validForPaymentMethod,
    }));
  }, [paymentMethod]);

  const setInvoiceContactInfo = (
    contactInfo: InvoiceContactInfo,
    valid: boolean
  ) => {
    setState((old) => ({
      ...old,
      invoiceContactInfo: contactInfo,
      valid: valid,
    }));
  };

  const reset = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [setState]);

  const fetchCostCalculation = useCallback(
    (
      paymentMethod: PaymentMethodE,
      discountKey: string | null,
      signupRequests: Array<SignupRequest>,
      competitionTag: string
    ) => {
      if (
        paymentMethod == state.lastFetchPaymentMethod &&
        discountKey == state.lastFetchDiscountSecret
      ) {
        return;
      }
      const paymentRequest: DisciplineSignupDto_POST_PUT = {
        paymentMethod: paymentMethod, //Enforce
        discountKey: discountKey ?? undefined,
        signups: signupRequests,
        //signups: props.signupUnits.flatMap((su) => su),
        invoice: undefined,
        swishNumber: "",
      };

      SignupClient.calculatePrise(competitionTag, paymentRequest).then(
        (response) => {
          setState((old) => {
            return {
              ...old,
              lastFetchDiscountSecret: discountKey,
              lastFetchPaymentMethod: paymentMethod,
              summary: response.body?.summary || "Kostnad saknas...",
              cost: response.body?.price || 0,
            };
          });
        }
      );
    },
    [state.lastFetchDiscountSecret, state.lastFetchPaymentMethod]
  );

  const overrideCost = useCallback(
    (newPrice: number | undefined) => {
      setState((old) => ({
        ...old,
        cost: newPrice,
      }));
    },
    [setState]
  );

  return {
    state,
    modifiers: {
      fetchCostCalculation,
      setInvoiceContactInfo,
      reset,
      overrideCost,
    },
  } as StateAndModifiers;
}

export function PaymentDetailsFinalizer(
  props: StateAndModifiers & { paymentMethod: PaymentMethodE | null }
) {
  const session = useSessionState();
  const maySetOwnPrice = session.hasAtLeastRole(Role.OFFICIAL);
  return (
    <>
      <CostBox
        summary={props.state.summary}
        cost={props.state.cost}
        maySetOwnPrice={maySetOwnPrice}
        overrideCost={props.modifiers.overrideCost}
      />
      {/**
       * 
      <Box
        padding="20px"
        paddingTop={"10px"}
        borderRadius={"10px"}
        shadow={"lg"}
        border={"2px"}
        borderColor={"gray.300"}
        background={"blue.50"}
      >
        <Text
          textAlign={"center"}
          marginBottom={"10px"}
          textDecor={"underline"}
        >
          Beräknad kostnad
        </Text>
        <Text whiteSpace={"pre"}>{props.state.summary}</Text>
        {maySetOwnPrice && (
          <HStack marginTop={"20px"}>
            <Text>Total: </Text>
            <InputGroup width={"50%"}>
              <Input
                background={"white"}
                value={props.state.cost}
                onChange={(e) => {
                  const newCost = parseInt(e.currentTarget.value);
                  if (newCost >= 0) {
                    props.modifiers.overrideCost(newCost);
                  } else {
                    props.modifiers.overrideCost(undefined);
                  }
                }}
              ></Input>
              <InputRightElement
                pointerEvents="none"
                children={<Text>SEK</Text>}
              />
            </InputGroup>
          </HStack>
        )}
      </Box>
       */}
      {props.paymentMethod == PaymentMethodE.INVOICE_MAN && (
        <InvoiceContactInfoComponent
          contactInfo={props.state.invoiceContactInfo}
          setInvoiceContactInfo={props.modifiers.setInvoiceContactInfo}
        />
      )}
    </>
  );
}

export const InvoiceContactInfoComponent = (props: {
  contactInfo: InvoiceContactInfo;
  setInvoiceContactInfo: (
    contactInfo: InvoiceContactInfo,
    valid: boolean
  ) => void;
}) => {
  const [newContactInfo, setNewContactInfo] = useState(props.contactInfo);
  const [validation, setValidation] = useState(
    validateContactInfo(props.contactInfo, PaymentMethodE.INVOICE_MAN)
  );

  useEffect(() => {
    const validation = validateContactInfo(
      newContactInfo,
      PaymentMethodE.INVOICE_MAN
    );
    setValidation(validation);
    props.setInvoiceContactInfo(
      newContactInfo,
      validation.validForPaymentMethod
    );
  }, [newContactInfo]);

  return (
    <VStack
      marginTop={"10px"}
      marginBottom={"0px"}
      border="1px"
      borderColor={"gray.500"}
      padding={"20px"}
      borderStyle={"double"}
    >
      <Text textDecor={"underline"}>Faktureringsuppgifter</Text>
      <FormControl isRequired isInvalid={!validation.name}>
        <FormLabel>Namn</FormLabel>
        <Input
          marginLeft={"5px"}
          type="text"
          placeholder="Namn"
          maxLength={50}
          value={props.contactInfo?.name}
          onChange={(e) => {
            const newName = e.target.value;
            setNewContactInfo((old) => {
              return { ...old, name: newName };
            });
          }}
        />
        <FormHelperText marginLeft={"10px"}>
          Företagets/föreningens namn.
        </FormHelperText>
      </FormControl>
      <FormControl isRequired isInvalid={!validation.email}>
        <FormLabel>Email</FormLabel>
        <Input
          marginLeft={"5px"}
          type="email"
          placeholder="Email"
          value={props.contactInfo?.email}
          onChange={(e) => {
            const newEmail = e.target.value
              .replaceAll(",", ".")
              .replaceAll(" ", "");
            setNewContactInfo((old) => {
              return { ...old, email: newEmail };
            });
          }}
        />
        <FormHelperText marginLeft={"10px"}>
          Emailadress för fakturering. Om inget annat anges i "övrigt" fältet
          nedan så skickas fakturan i första hand till denna epostadressen.
        </FormHelperText>
      </FormControl>
      <FormControl isRequired isInvalid={!validation.organizationNumber}>
        <FormLabel>Organisationsnummer</FormLabel>
        <Input
          marginLeft={"5px"}
          type="text"
          placeholder="Organisationsnummer (10-12 siffror)"
          value={props.contactInfo?.organizationNumber}
          onChange={(e) => {
            const newOrganizationNumber = e.target.value;
            setNewContactInfo((old) => {
              return { ...old, organizationNumber: newOrganizationNumber };
            });
          }}
        />
      </FormControl>
      <FormControl isRequired isInvalid={!validation.address}>
        <FormLabel>Faktureringsadress</FormLabel>
        <Textarea
          marginLeft={"5px"}
          placeholder="Adress"
          maxLength={150}
          value={props.contactInfo?.address}
          onChange={(e) => {
            const newAddress = e.target.value;
            setNewContactInfo((old) => {
              return { ...old, address: newAddress };
            });
          }}
        />
      </FormControl>
      <FormControl isRequired isInvalid={!validation.phone}>
        <FormLabel>Telefon</FormLabel>
        <Input
          marginLeft={"5px"}
          type="text"
          placeholder="Telefonnummer"
          value={props.contactInfo?.phone}
          onChange={(e) => {
            const newPhone = e.target.value.replaceAll(/[^+0-9 ]/g, "");
            setNewContactInfo((old) => {
              return { ...old, phone: newPhone };
            });
          }}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Övrigt</FormLabel>
        <Textarea
          marginLeft={"5px"}
          placeholder="Övrig information/önskemål, t.ex märkning/ordernr/referens (valfritt, max 500 tecken)"
          maxLength={500}
          value={props.contactInfo?.comment}
          onChange={(e) => {
            const newComment = e.target.value;
            setNewContactInfo((old) => {
              return { ...old, comment: newComment };
            });
          }}
        />
      </FormControl>
    </VStack>
  );
};

function validateContactInfo(
  contactInfo: InvoiceContactInfo,
  paymentMethod: PaymentMethodE | null | undefined
) {
  if (paymentMethod == null) {
    return {
      name: false,
      email: false,
      phone: false,
      address: false,
      organizationNumber: false,
      comment: false,
      validForPaymentMethod: false,
    };
  }

  const validName =
    contactInfo.name != null &&
    contactInfo.name.length > 2 &&
    contactInfo.name.length <= 50;
  const validEmail =
    contactInfo.email != null &&
    contactInfo.email.length < 250 &&
    contactInfo.email.includes("@");
  const validPhone =
    contactInfo.phone != null && contactInfo.phone.length <= 20;
  const validAddress =
    contactInfo.address != null &&
    contactInfo.address.length > 0 &&
    contactInfo.address.length <= 150;
  const validOrganizationNumber =
    contactInfo.organizationNumber != null &&
    contactInfo.organizationNumber
      .trim()
      .replaceAll("-", "")
      .match(/^\d{10,12}$/) != null;
  const validComment =
    contactInfo.comment == null || contactInfo.comment.length <= 500;
  const validationResult = {
    name: validName,
    email: validEmail,
    phone: validPhone,
    address: validAddress,
    organizationNumber: validOrganizationNumber,
    comment: validComment,
    validForPaymentMethod:
      paymentMethod != PaymentMethodE.INVOICE_MAN ||
      (validName &&
        validEmail &&
        validPhone &&
        validAddress &&
        validOrganizationNumber &&
        validComment),
  };
  //console.log("Validation result: ", validationResult);
  return validationResult;
}

export function CostBox(props: {
  summary: string;
  cost: number | undefined;
  maySetOwnPrice: boolean;
  overrideCost: (newCost: number | undefined) => void;
}) {
  const handleCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCost = parseInt(e.currentTarget.value);
    if (!isNaN(newCost) && newCost >= 0) {
      props.overrideCost(newCost);
    } else {
      props.overrideCost(undefined);
    }
  };

  const Content = (
    <Box
      padding="20px"
      paddingTop="10px"
      borderRadius="10px"
      shadow="lg"
      border="2px"
      borderColor="gray.300"
      background="blue.50"
    >
      <Text textAlign="center" mb="10px" textDecor="underline">
        Beräknad kostnad
      </Text>
      <Text whiteSpace="pre">{props.summary}</Text>
    </Box>
  );

  const Edit = (
    <Box
      padding="20px"
      paddingTop="10px"
      borderRadius="10px"
      shadow="lg"
      border="2px"
      borderColor="gray.300"
      background="blue.50"
    >
      <Text textAlign="center" mb="10px" textDecor="underline">
        Ange egen kostnad
      </Text>
      <HStack marginTop="10px">
        <Text>Total:</Text>
        <InputGroup width="50%">
          <Input
            background="white"
            type="number"
            value={props.cost ?? ""}
            onChange={handleCostChange}
          />
          <InputRightElement pointerEvents="none">
            <Text>SEK</Text>
          </InputRightElement>
        </InputGroup>
      </HStack>
    </Box>
  );

  return props.maySetOwnPrice ? (
    <Tabs variant="enclosed" colorScheme="blue" isFitted>
      <TabList mb="1em">
        <Tab>Beräknad kostnad</Tab>
        <Tab>Sätt annan kostnad</Tab>
      </TabList>
      <TabPanels>
        <TabPanel>{Content}</TabPanel>
        <TabPanel>{Edit}</TabPanel>
      </TabPanels>
    </Tabs>
  ) : (
    Content
  );
}
